import React from 'react';
import { Document, View } from '@react-pdf/renderer';
import type { ModuleId, ReportData } from '../tokens/types';
import { MODULES, MODULE_META_EXTRA } from '../modules/moduleRegistry';
import { PdfCover, PdfPage } from '../primitives/PdfPrimitives';
import { PdfDisclaimer } from '../primitives/DisclaimerPage';
import { PdfToc, type TocEntry } from '../primitives/TocPage';
import { RenderTargetProvider } from '../primitives/RenderTarget';
import { PdfDirectorDetail } from '../modules/Management';
import { fmtDate } from '../modules/formatUtils';
import type { PdfLayoutOverrides } from '../tokens/pdfLayout';
import '../tokens/pdfFonts';

// Rendered-height ceiling per module (pt) used only for flow-mode packing.
// A4 usable content is 684pt after margin tightening. Estimates sit near the
// 75th-percentile observed render height (NOT the median) so packing stays
// conservative and modules don't overflow into the footer area.
//
// If a module's content exceeds its estimate, react-pdf's `wrap` will move the
// overflow to the next page — but only at safe child boundaries. Underestimating
// here was the root cause of cross-section bleed, so values err high.
export const MODULE_PDF_HEIGHT: Partial<Record<ModuleId, number>> = {
    identity: 200,
    score: 700,
    riskParameters: 420,
    overview: 780,
    // 2-col card grid (~152pt each row) + employees block (~140pt). 4 dirs = ~440pt;
    // bump above that so packIntoPages doesn't squeeze anything else onto the page.
    management: 540,
    directorsDeep: 720,
    directorBureau: 320,
    relatedParties: 360,
    funding: 280,
    mca: 160,
    // Card grid with donut + bar charts — actual height scales with row count.
    // Bump well above PAGE_CONTENT_HEIGHT so it always gets its own page;
    // packing it with another tall module triggers a react-pdf yoga crash
    // when wrap={false} card rows overflow under tight space constraints.
    mcaCharges: 700,
    compliances: 320,
    financialMetrics: 340,
    auditedStatements: 540,
    // Regulatory matrix (2-up cards × N entities) + data tables — same
    // overflow risk as mcaCharges. Force to its own page.
    legalRisk: 700,
    alerts: 600,
};
// 842pt (A4 height) − 79pt (paddingTop) − 79pt (paddingBottom) = 684pt usable.
// Conservative ceiling — packIntoPages stops adding before this is hit so a module
// that under-estimates its height by up to ~30pt still won't bleed into the footer.
export const PAGE_CONTENT_HEIGHT = 660;
const MODULE_GAP = 14;

type RenderableModule = (typeof MODULES)[keyof typeof MODULES];

const packIntoPages = (modules: RenderableModule[]): RenderableModule[][] => {
    const pages: RenderableModule[][] = [];
    let current: RenderableModule[] = [];
    let currentHeight = 0;
    for (const m of modules) {
        const h = MODULE_PDF_HEIGHT[m.meta.id] ?? 280;
        if (current.length === 0) {
            current.push(m);
            currentHeight = h;
            continue;
        }
        if (currentHeight + MODULE_GAP + h > PAGE_CONTENT_HEIGHT) {
            pages.push(current);
            current = [m];
            currentHeight = h;
        } else {
            current.push(m);
            currentHeight += MODULE_GAP + h;
        }
    }
    if (current.length) pages.push(current);
    return pages;
};

export type ReportDocumentProps = {
    data: ReportData;
    enabledModules: ModuleId[];
    generatedOn?: string;
    pnoLogoUrl?: string;
    /**
     * Optional layout overrides (paper size, paddings, module gap). Defaults
     * to A4 with the historical tuning. The drawer flips this to `paperSize:
     * 'A3'` on the automatic fallback retry when the default A4 render fails.
     */
    layoutOverrides?: PdfLayoutOverrides;
};

export const ReportDocument: React.FC<ReportDocumentProps> = ({ data, enabledModules, generatedOn, pnoLogoUrl, layoutOverrides }) => {
    const today = generatedOn ?? fmtDate(new Date().toISOString());
    const meta: Array<{ label: string; value: string }> = [];
    if (data.identity.cin && data.identity.cin !== '—') meta.push({ label: 'CIN', value: data.identity.cin });
    if (data.identity.pan && data.identity.pan !== '—') meta.push({ label: 'PAN', value: data.identity.pan });
    if (data.identity.sector && data.identity.sector !== '—') meta.push({ label: 'Sector', value: data.identity.sector });
    if (data.identity.state) meta.push({ label: 'HQ', value: data.identity.state });
    if (data.identity.foundationDate) meta.push({ label: 'Founded', value: data.identity.foundationDate });
    if (data.score?.companyScore && Number.isFinite(data.score.companyScore) && data.score.companyScore > 0) {
        const scaled = (data.score.companyScore / 4) * 10;
        meta.push({ label: 'AICA Risk Score', value: `${scaled.toFixed(1)} / 10` });
    }

    const renderable = enabledModules.map((id) => MODULES[id as keyof typeof MODULES]).filter((m) => m && m.hasData(data));

    const tocEntries: TocEntry[] = renderable.map((m, i) => ({
        index: String(i + 1).padStart(2, '0'),
        title: m.meta.title,
        subtitle: m.meta.description,
        coverage: MODULE_META_EXTRA[m.meta.id]?.coverage,
    }));

    return (
        <Document
            title={`AICA Credit Report — ${data.identity.orgName}`}
            author="Recur Club"
            subject={`Credit intelligence report for ${data.identity.orgName}`}
        >
            <PdfCover
                orgName={data.identity.orgName}
                subtitle={[data.identity.sector, data.identity.state].filter(Boolean).join(' · ')}
                meta={meta}
                generatedOn={today}
                pnoLogoUrl={pnoLogoUrl}
                layout={layoutOverrides}
            />
            {tocEntries.length > 0 ? <PdfToc orgName={data.identity.orgName} entries={tocEntries} layout={layoutOverrides} /> : null}
            {packIntoPages(renderable).map((group, gi) => {
                const sectionLabel =
                    group.length === 1
                        ? `${String(renderable.indexOf(group[0]) + 1).padStart(2, '0')} · ${group[0].meta.title}`
                        : `${String(renderable.indexOf(group[0]) + 1).padStart(2, '0')}–${String(
                              renderable.indexOf(group[group.length - 1]) + 1,
                          ).padStart(2, '0')}`;

                // If this group contains the management module, emit one extra
                // PdfPage per director RIGHT AFTER it. Director detail pages
                // share the management module's TOC index (e.g. "05") in their
                // chrome label, since they belong to the same section.
                const mgmtModule = group.find((m) => m.meta.id === 'management');
                const directors = mgmtModule && data.management ? data.management.profiles : [];
                const mgmtIndex = mgmtModule ? String(renderable.indexOf(mgmtModule) + 1).padStart(2, '0') : '';

                return (
                    <React.Fragment key={`flow-${gi}`}>
                        <PdfPage orgName={data.identity.orgName} sectionLabel={sectionLabel} layout={layoutOverrides}>
                            {group.map((m, i) => {
                                const Mod: any = m.Pdf;
                                const globalIdx = renderable.indexOf(m);
                                const index = String(globalIdx + 1).padStart(2, '0');
                                return (
                                    <View key={m.meta.id} style={{ marginBottom: i < group.length - 1 ? 18 : 0 }}>
                                        <RenderTargetProvider target="pdf">
                                            <Mod data={data} index={index} breakBefore={false} />
                                        </RenderTargetProvider>
                                    </View>
                                );
                            })}
                        </PdfPage>
                        {directors.map((profile, di) => (
                            <PdfPage
                                key={`director-${di}`}
                                orgName={data.identity.orgName}
                                sectionLabel={`${mgmtIndex} · Director Detail`}
                                layout={layoutOverrides}
                            >
                                <RenderTargetProvider target="pdf">
                                    <PdfDirectorDetail
                                        profile={profile}
                                        moduleIndex={mgmtIndex}
                                        directorIndex={di + 1}
                                        directorTotal={directors.length}
                                        data={data}
                                    />
                                </RenderTargetProvider>
                            </PdfPage>
                        ))}
                    </React.Fragment>
                );
            })}
            <PdfPage orgName={data.identity.orgName} sectionLabel="Disclaimer" layout={layoutOverrides}>
                <PdfDisclaimer orgName={data.identity.orgName} />
            </PdfPage>
        </Document>
    );
};
